/**
 * Wire types, mirroring the backend DTOs one-for-one. Hand-written rather than generated: the
 * API is small and stable, and a mismatch shows up immediately in the components that read it.
 *
 * Timestamps are ISO-8601 strings with an offset (Java `OffsetDateTime`), never `Date` — they
 * stay strings until something actually formats them.
 */

export type NotificationChannel = 'EMAIL' | 'TELEGRAM' | 'NTFY' | 'PUSHOVER';

export type EventStatus = 'PENDING_REVIEW' | 'PUBLISHED' | 'REJECTED' | 'ARCHIVED';

/**
 * How an organiser's identity was established, weakest to strongest. Replaced a boolean that said
 * the same thing for "an operator glanced at their site" and "they proved control of the account".
 */
/**
 * Where a listing came from — never a judgement about whether it's safe.
 *
 * There used to be a middle tier meaning "an operator checked them". It was removed: shown to the
 * public it read as an assurance, and no check was ever performed that could stand behind one.
 */
export type OrganiserVerification = 'UNVERIFIED' | 'PLATFORM_LINKED';

/** GET /api/events, GET /api/events/{id} — the public view. No submitter PII. */
/**
 * One age band an event caters for. A null bound means no limit.
 *
 * <p>An **empty list** means the event states no age policy — not that nobody is welcome. Most
 * events come from linked Eventbrite accounts, which carry no age data at all.
 */
export interface AgeRange {
  minAge: number | null;
  maxAge: number | null;
}

/** "18–25", "30 and over", "up to 25". */
export function describeAgeRange({ minAge, maxAge }: AgeRange): string {
  if (minAge !== null && maxAge !== null) return `${minAge}–${maxAge}`;
  if (minAge !== null) return `${minAge} and over`;
  return `up to ${maxAge}`;
}

export interface EventSummary {
  id: number;
  title: string;
  /**
   * Null on the listing endpoint whether or not the event has one — it's the largest field on an
   * event and nothing in the results renders it, so it isn't sent. Populated by
   * `GET /api/events/{id}`, which serves the page that does. Keyword filtering reads titles, not
   * this, so nothing on the results page is weaker for its absence.
   */
  description: string | null;
  startTime: string;
  endTime: string | null;
  timezone: string;
  venueName: string | null;
  addressLine: string | null;
  city: string | null;
  postcode: string | null;
  country: string;
  latitude: string | null;
  longitude: string | null;
  externalUrl: string | null;
  organiser: string | null;
  /** Empty means no stated age policy — open to everyone, not closed to everyone. */
  ageRanges: AgeRange[];
  /**
   * What the organiser says this event is. Empty for anything imported from a ticketing platform,
   * which is most of the catalogue — the filter chips fall back to reading words out of titles
   * when an event has none.
   */
  keywords: string[];
  /** Null when the event lists no tickets — nobody said, rather than free. */
  price: PriceRange | null;
  /** Only populated for a single event; list responses leave it empty. */
  tickets: EventTicket[];
  organiserVerification: OrganiserVerification | null;
  /**
   * We added this listing ourselves from information published elsewhere; the organiser neither
   * sent it nor connected an account. Distinct from `organiserVerification`, which is UNVERIFIED
   * for this *and* for a stranger's form submission — two different claims that must not be shown
   * as the same one.
   */
  addedByUs: boolean;
  /**
   * We worked this address out from a venue name the organiser published without one, rather than
   * being told it. Shown to the reader: an inferred address printed in the organiser's voice is
   * our guess wearing their authority, and a provisional venue would send someone to the wrong
   * building on our say-so.
   */
  addressResolved: boolean;
}

/**
 * A bookmark — deliberately not an {@link EventSummary}.
 *
 * Events are removed from the catalogue 30 days after they finish, but the bookmark survives with
 * a copy of what it needs to display. So a saved event may have no `eventId` and nothing to link
 * to, which a full event shape couldn't express.
 */
export interface SavedEventSummary {
  /** The bookmark's own id — how it's removed, since `eventId` may be null. */
  id: number;
  /** Null once the event has been removed from the catalogue. */
  eventId: number | null;
  title: string;
  startTime: string;
  city: string | null;
  externalUrl: string | null;
  /** False when the event is gone: show it, but don't offer a link to a page that no longer exists. */
  stillListed: boolean;
  savedAt: string;
}

export type AttendeeType = 'PARTICIPANT' | 'COMPANION';

/** One price an event charges. `amountMinor` is pence — 3500 is £35.00. */
export interface EventTicket {
  label: string;
  attendeeType: AttendeeType;
  minAge: number | null;
  maxAge: number | null;
  amountMinor: number;
  currency: string;
}

/**
 * What a listing costs, across its participant tickets only.
 *
 * Null on an event means nobody told us — which is not the same as free, and must never render the
 * same way. Free is `free: true`, and worth saying out loud.
 */
export interface PriceRange {
  minMinor: number;
  maxMinor: number;
  currency: string;
  free: boolean;
}

/** "£35", "from £30", "Free". Minor units in, one string out. */
export function describePrice(price: PriceRange | null | undefined): string | null {
  if (!price) return null;
  if (price.free) return 'Free';
  const format = (minor: number) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: price.currency,
      // Whole pounds are the norm for these events; showing "£35.00" everywhere is noise, but a
      // price of £32.50 must not silently round to £33.
      minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    }).format(minor / 100);

  return price.minMinor === price.maxMinor ? format(price.minMinor) : `from ${format(price.minMinor)}`;
}

/** One place on the homepage, with how many upcoming events it has. */
export interface CityCount {
  name: string;
  count: number;
  /** The middle of its venues. Null when none could be geocoded — it then gets no map bubble. */
  latitude: number | null;
  longitude: number | null;
}

/** GET /api/events/overview — the homepage before anyone searches. */
export interface EventOverview {
  totalEvents: number;
  organiserCount: number;
  /** Two or more events each, busiest first. */
  cities: CityCount[];
  /** One event each; shown together as a single row. */
  otherCities: CityCount[];
  /** The soonest few. */
  upcoming: EventSummary[];
}

export interface EventQuery {
  country?: string;
  lat?: number;
  lng?: number;
  radiusMeters?: number;
}

/** POST /api/events/submit */
export interface EventSubmission {
  title: string;
  description?: string;
  /** What the event is — matched against what subscribers asked to hear about. */
  keywords?: string[];
  startTime: string;
  endTime: string;
  organiserName: string;
  venueName: string;
  addressLine: string;
  city?: string;
  postcode: string;
  country?: string;
  externalUrl?: string;
  /**
   * Omitted when signed in: the server takes the submitter's details from their account, so
   * sending them would at best duplicate what it already knows and at worst disagree with it.
   */
  submitterName?: string;
  submitterEmail?: string;
  /** Optional, and several are allowed — organisers often run parallel sessions. */
  ageRanges?: AgeRange[];
  /**
   * Optional. Sending none means "not stated", which the site shows as exactly that — never as
   * free, which is a ticket priced at zero.
   */
  tickets?: EventTicket[];
  /**
   * Where an operator found this listing. Read only on the operator's own endpoints — a public
   * submission carrying one is ignored, because an organiser sending their own event didn't find
   * it anywhere and nobody has checked the claim.
   */
  foundAtUrl?: string;
  /** Operator-only, like foundAtUrl. Omitted means false — nothing is qualified by default. */
  addressResolved?: boolean;
}

export interface EventSubmissionResult {
  id: number;
  status: EventStatus;
  message: string;
}

/** GET /api/me, POST /api/auth/verify */
export interface CurrentUser {
  id: number;
  email: string;
  /** Null until someone sets one — sign-up only asks for an email. Fall back to the address. */
  displayName: string | null;
  roles: string[];
}

export interface MessageResult {
  message: string;
}

/** /api/me/subscriptions */
export interface Subscription {
  id: number;
  enabled: boolean;
  keywords: string[];
  channels: NotificationChannel[];
  city: string | null;
  country: string | null;
  latitude: string | null;
  longitude: string | null;
  radiusMeters: number | null;
  /** The ages the events should be for — not the subscriber's own age. */
  wantedMinAge: number | null;
  wantedMaxAge: number | null;
  createdAt: string;
}

export interface SubscriptionInput {
  keywords: string[];
  channels: NotificationChannel[];
  city?: string | null;
  country?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  radiusMeters?: number | null;
  wantedMinAge?: number | null;
  wantedMaxAge?: number | null;
  enabled?: boolean;
}

/** GET /api/me/export — shape is whatever GdprService assembles; treated as opaque JSON. */
export type AccountExport = Record<string, unknown>;

/** /api/me/organiser-links — an organiser's connected platform accounts. Never carries a token. */
export type OrganiserLinkStatus = 'ACTIVE' | 'REVOKED';

export interface OrganiserLink {
  id: number;
  provider: 'EVENTBRITE';
  externalOrgId: string;
  displayName: string | null;
  organiser: string | null;
  status: OrganiserLinkStatus;
  lastError: string | null;
  linkedAt: string;
}

/** GET /api/me/notifications — what was actually sent to this account. */
export interface NotificationItem {
  id: number;
  type: string;
  eventId: number;
  eventTitle: string;
  eventStartTime: string | null;
  sentAt: string;
  read: boolean;
}

/** GET /api/me/events — an event as its own submitter sees it, statuses and all. */
export interface MyEvent {
  id: number;
  title: string;
  startTime: string;
  venueName: string | null;
  city: string | null;
  status: EventStatus;
  rejectionReason: string | null;
  organiser: string | null;
  /** True when it arrived automatically from a platform they linked, rather than by hand. */
  fromLinkedAccount: boolean;
  ageRanges: AgeRange[];
  submittedAt: string;
}

/** A platform an organiser asked us to support, because we can't link it yet. */
export interface PlatformRequest {
  id: number;
  platform: string;
  profileUrl: string | null;
  requestedAt: string;
}

/** GET /api/admin/users — counts only, never the contents of someone's alerts. */
export interface AdminUser {
  id: number;
  email: string;
  displayName: string | null;
  enabled: boolean;
  emailVerified: boolean;
  /** From the config allow-list, not a database role — so it can't be granted from the UI. */
  admin: boolean;
  alertCount: number;
  submittedEventCount: number;
  linkedAccountCount: number;
  joinedAt: string;
}

/**
 * GET /api/admin/discoveries — an event on a public listing page that the catalogue hasn't got.
 * An address and a name read out of it; the listing itself is still written from the organiser's
 * own page.
 */
export interface AdminDiscovery {
  id: number;
  url: string;
  name: string;
  /** The listing page it was spotted on. */
  foundAtUrl: string;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * GET /api/admin/subscriptions — one row per person with alerts. Who, how many and how they're
 * delivered; never what the alerts ask for.
 */
export interface AdminSubscriber {
  accountId: number;
  email: string;
  displayName: string | null;
  /** False when the account is suspended, so none of its alerts send. */
  accountEnabled: boolean;
  alerts: number;
  /** Switched on; the rest are paused by the subscriber. */
  activeAlerts: number;
  channels: Array<'EMAIL' | 'TELEGRAM' | 'NTFY' | 'PUSHOVER'>;
  firstSubscribedAt: string | null;
  lastSubscribedAt: string | null;
}

/** GET /api/admin/analytics/platform-demand — what to integrate next, by how many asked. */
export interface PlatformDemand {
  platform: string;
  requests: number;
}

/** GET /api/admin/analytics/summary — aggregates only; no per-visitor rows ever leave the server. */
export interface AnalyticsSummary {
  days: number;
  visitors: number;
  views: number;
  averageVisitDurationMs: number | null;
  viewsPerVisitor: number;
  byDay: Array<{ day: string; visitors: number; views: number }>;
  byCountry: Array<{ country: string; visitors: number; views: number }>;
  topPages: Array<{ path: string; views: number; visitors: number; averageDurationMs: number | null }>;
  topReferrers: Array<{ referrer: string; visitors: number }>;
}

/** GET /api/admin/events — includes the moderation context the public view omits. */
export interface AdminEvent {
  id: number;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string | null;
  venueName: string | null;
  addressLine: string | null;
  city: string | null;
  postcode: string | null;
  country: string;
  externalUrl: string | null;
  status: EventStatus;
  organiser: string | null;
  claimedOrganiser: string | null;
  submitterName: string | null;
  submitterEmail: string | null;
  rejectionReason: string | null;
  submittedAt: string;
  /** Sent by the API since the DTO was written; the type simply never declared it. */
  ageRanges: AgeRange[];
  /** Whether the event's own page still answers. Only ever checked for MANUAL listings. */
  linkStatus: 'OK' | 'MOVED' | 'GONE' | 'UNREACHABLE' | null;
  linkCheckedAt: string | null;
  linkFailures: number;
  /**
   * The last four exist so the edit form can be filled in from what is already stored. Editing
   * replaces the whole event, so a form that couldn't show the current keywords or tickets would
   * silently delete them the first time somebody fixed a typo.
   */
  timezone: string;
  keywords: string[];
  tickets: EventTicket[];
  sourceType: 'EVENTBRITE_ORGANIZER' | 'GENERIC_WEBSITE' | 'MANUAL' | 'USER_SUBMISSION' | null;
  /**
   * The page this listing was taken from, when an operator added it by hand — not `externalUrl`,
   * which is where you buy a ticket. An event found on an aggregator and sold through Eventbrite
   * has both, and only this one identifies whose collection the entry came out of.
   */
  foundAtUrl: string | null;
  /** Sent back so an edit can't quietly un-qualify an address we worked out. */
  addressResolved: boolean;
}

/** GET /api/admin/sources — one site the hand-compiled catalogue has drawn from. */
export interface HostRow {
  host: string;
  /** Other hostnames folded into this row, so a merged count shows its working. */
  aliasedFrom: string[];
  events: number;
  sharePercent: number;
  /** Whose events they are. The count is what separates a busy platform from one programme. */
  organisers: string[];
  crowded: boolean;
  /** Rows here whose host was inferred from the booking link rather than recorded. */
  withoutProvenance: number;
  firstAdded: string | null;
  lastAdded: string | null;
}

/**
 * One organiser whose events we listed without being asked.
 *
 * The axis that actually answers the question: a programme is a small collection, so a handful of
 * events can be most of it — where the same handful off a ticketing platform is nothing.
 */
export interface OrganiserRow {
  organiser: string;
  events: number;
  sharePercent: number;
  /** Where they were found — one organiser across three sites is invisible on the host axis. */
  hosts: string[];
  crowded: boolean;
  firstAdded: string | null;
  lastAdded: string | null;
}

/**
 * How concentrated the hand-compiled catalogue is, along two axes that mean different things.
 *
 * Counts only listings an operator compiled: an event its organiser sent us, or one that arrived
 * through a linked ticketing account, was given rather than taken, and counting it here would
 * dilute the only number this report exists to show.
 */
export interface SourceReport {
  compiled: number;
  hosts: number;
  organisers: number;
  /** Thresholds the server flags at, sent so the panel keeps no copy of its own. */
  organiserCrowdedAt: number;
  hostCrowdedAt: number;
  unrecorded: number;
  hostRows: HostRow[];
  organiserRows: OrganiserRow[];
}

/** A compiled listing with no recorded source, from GET /api/admin/sources/unrecorded. */
export interface UnrecordedListing {
  id: number;
  title: string;
  organiser: string | null;
  externalUrl: string | null;
}

export interface ModerationOutcome {
  id: number;
  status: EventStatus;
  organiser: string | null;
  rejectionReason: string | null;
}
